// The calendar (spec/dashboard.md section 8.5): a day-column timeline with lanes for events, offers, sales and A/B tests;
// the bars derived from the time leaves of each owner's condition (bars.ts), the duration tail, the now line, the revenue
// row and the players figure of the selected environment; M7 slice 7: the daily KPI rows (unique and new users, sessions,
// playtime, ARPDAU) beside the revenue row, the bars' user counts, the metrics panel over the shared analytics range with
// "Include testers", and the Export… dialog (an analytics.export job and its download). The offers lane: an offer (or an
// offer group) has no condition of its own, so its window is the union of the windows of the game events whose `script`
// attaches it (the offers workspace's attachedBy map); an offer no event attaches is "not attached". Click selects a bar
// and opens the side panel (name, template, the window in words, the condition, the attaching events, Open); double-click
// and Enter open the editor; Escape or the panel's close clears the selection.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router';
import type { Document, Json } from '../../api/types';
import { barsOf, tailsOf, unionBars, windowOf, type BarSet, type Interval, type Window } from '../../content/calendar/bars';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { useContent } from '../../content/ContentContext';
import { DocumentEditorHost } from '../../content/DocumentEditor';
import { EnvSelector, useLiveOpsEnv } from '../../content/liveops/env';
import { n, useComputedText, useDefinitionText, useEntityFigures, type EntityKind } from '../../content/liveops/analytics';
import { moneyText, useAnalyticsDaily, useRevenue, useSegmentSizes, utcDate } from '../../content/liveops/stats';
import { createAbTest } from '../../content/overrides/create';
import { AB_TEST } from '../../content/overrides/model';
import { useDocumentList } from '../../content/useDocumentList';
import { createDocumentDraft, heirsOf, useEditorHost, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, MoreVertical, Plus, Repeat, Search, Settings, User, X } from '../../ui/icons';
import { Switch, TextInput } from '../../ui/inputs';
import { Menu } from '../../ui/Popover';
import { Sparkline } from '../../ui/Sparkline';
import { formatDuration, formatInstant, formatInstantWithZone, useTimeZone, type DisplayZone } from '../../ui/time';
import { useAnalyticsRange } from '../../workspace/analyticsRange';
import { AnalyticsExportDialog } from './AnalyticsExportDialog';
import { EVENT, GROUP, OFFER } from './cards';
import { GroupEventWizard } from './EventsPage';
import { OfferWizard } from './OfferWizard';
import { RangePicker } from './RangePicker';
import { SALE } from './SalesPage';
import './calendar.css';

const DAY = 86400;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Entry {
  doc: Document;
  lane: string;
  label: string;
  colour: string;
  bars: Interval[];
  tails: Interval[];
  hasTime: boolean;
  never: boolean;
  /** the analytics kind of the bar's user count (none for sales) */
  kind: EntityKind | null;
  /** an offer's or a group's attaching events (null outside the offers lane) */
  attached: Document[] | null;
}

/** A document's span in the window: its bars and, for the offers lane, the events that attach it. */
interface Span {
  bars: BarSet;
  attached: Document[] | null;
}

const dataOf = (doc: Document) => (doc.data ?? {}) as Record<string, Json>;

const LANE_KIND: Record<string, EntityKind | null> = { events: 'event', offers: 'offer', sales: null, tests: 'ab_test' };

/** A stable colour per template for documents without `meta.calendarColor`. */
export function autoColour(template: string): string {
  let h = 0;
  for (const ch of template) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `hsl(${h % 360} 60% 42%)`;
}

export function zoneOffsetOf(zone: DisplayZone, offsets: { dev?: number; stage?: number }): number {
  if (zone === 'utc') return 0;
  if (zone === 'env:dev') return offsets.dev ?? 0;
  if (zone === 'env:stage') return offsets.stage ?? 0;
  return -new Date().getTimezoneOffset() * 60;
}

export function CalendarPage() {
  return (
    <DocumentEditorHost>
      <Calendar />
    </DocumentEditorHost>
  );
}

function Calendar() {
  const catalog = useContent();
  const env = useWidgetEnv();
  const host = useEditorHost();
  const { org, game, offsets, can } = useGame();
  const { branch = '' } = useParams();
  const [zone] = useTimeZone();
  const offset = zoneOffsetOf(zone, offsets);
  const { env: figuresEnv } = useLiveOpsEnv();
  const now = Math.floor(Date.now() / 1000);
  const [compact, setCompact] = useState(false);
  const days = compact ? 30 : 15;
  const [start, setStart] = useState(() => windowOf(now - 2 * DAY, 1, offset).start);
  const window = useMemo<Window>(() => ({ start, end: start + days * DAY }), [start, days]);
  const [calendarOnly, setCalendarOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const [wizard, setWizard] = useState<null | 'offer' | 'group'>(null);
  const [exporting, setExporting] = useState(false);
  const events = useDocumentList({ template: EVENT, includeHeirs: true, pageSize: 200 });
  // the offers and groups of every template of the family (a game's own offer template is an heir), as the offers workspace lists them
  const offers = useDocumentList({ template: OFFER, includeHeirs: true, pageSize: 200 });
  const groups = useDocumentList({ template: GROUP, includeHeirs: true, pageSize: 200 });
  const sales = useDocumentList({ template: SALE, includeHeirs: false, pageSize: 200 });
  const tests = useDocumentList({ template: AB_TEST, includeHeirs: false, pageSize: 200 });
  const sizes = useSegmentSizes(figuresEnv);
  const windowFrom = utcDate(window.start);
  const windowTo = utcDate(window.end - 1);
  const revenue = useRevenue(figuresEnv, windowFrom, windowTo);
  const { range } = useAnalyticsRange(figuresEnv);
  const testers = range.includeTesters;
  const daily = useAnalyticsDaily(figuresEnv, windowFrom, windowTo, testers);
  const period = useAnalyticsDaily(figuresEnv, range.from, range.to, testers);
  const define = useDefinitionText();
  const computed = useComputedText();
  const canWrite = can('content_write') && !env.workspace.isProtected;

  const lanes = useMemo(
    () => [
      { key: 'events', label: 'Game events', noun: 'game event', docs: events.rows, loading: events.loading },
      { key: 'offers', label: 'Offers', noun: 'offer or offer group', docs: [...offers.rows, ...groups.rows], loading: offers.loading || groups.loading },
      { key: 'sales', label: 'Sales', noun: 'sale', docs: sales.rows, loading: sales.loading },
      { key: 'tests', label: 'A/B tests', noun: 'A/B test', docs: tests.rows, loading: tests.loading },
    ],
    [events.rows, offers.rows, groups.rows, sales.rows, tests.rows, events.loading, offers.loading, groups.loading, sales.loading, tests.loading],
  );
  const templates = useMemo(() => Array.from(new Set(lanes.flatMap((l) => l.docs.map((d) => d.template)))).sort(), [lanes]);
  // the events attaching each offer or group through `script` (OffersWorkspace's attachedBy), over every event, filters aside
  const attachedBy = useMemo(() => {
    const map = new Map<string, Document[]>();
    for (const e of events.rows) {
      const target = dataOf(e).script;
      if (typeof target === 'string') map.set(target, [...(map.get(target) ?? []), e]);
    }
    return map;
  }, [events.rows]);
  const spanOf = useMemo(() => {
    const own = (doc: Document) => {
      const c = dataOf(doc).condition;
      return barsOf(catalog, typeof c === 'string' ? c : null, window, offset);
    };
    return (laneKey: string, doc: Document): Span => {
      if (laneKey === 'offers') {
        const attached = attachedBy.get(doc.id) ?? [];
        return { bars: unionBars(attached.map(own)), attached };
      }
      let bars = own(doc);
      if (laneKey === 'tests' && !bars.hasTime) {
        const d = dataOf(doc);
        const status = Number(d.status ?? 0);
        const finish = Number(d.finishTime ?? 0);
        if ((status === 1 || status === 4) && finish > now) bars = { intervals: [{ start: Math.max(now, window.start), end: Math.min(finish, window.end) }].filter((i) => i.end > i.start), hasTime: true, never: false };
      }
      return { bars, attached: null };
    };
  }, [catalog, window, offset, attachedBy, now]);
  const colourOf = (doc: Document) => ((doc.meta ?? {}) as { calendarColor?: string }).calendarColor ?? autoColour(doc.template);
  const entries = useMemo<Record<string, Entry[]>>(() => {
    const out: Record<string, Entry[]> = {};
    const q = search.trim().toLowerCase();
    for (const lane of lanes) {
      out[lane.key] = [];
      for (const doc of lane.docs) {
        if (hidden.has(doc.template)) continue;
        const label = catalog.label(doc);
        if (q && !label.toLowerCase().includes(q)) continue;
        const { bars, attached } = spanOf(lane.key, doc);
        // an offer no event attaches has no window at all: hidden under "Calendar events only", "not attached" otherwise
        if (calendarOnly && !bars.hasTime) continue;
        const duration = lane.key === 'events' ? Number(dataOf(doc).duration ?? 0) : 0;
        const kind = lane.key === 'offers' && doc.template === GROUP ? 'offer_group' : (LANE_KIND[lane.key] ?? null);
        out[lane.key]!.push({ doc, lane: lane.key, label, colour: colourOf(doc), bars: bars.intervals, tails: tailsOf(bars.intervals, duration, window), hasTime: bars.hasTime, never: bars.never, kind, attached });
      }
    }
    return out;
  }, [lanes, hidden, search, catalog, window, calendarOnly, spanOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // click = select: the side panel shows the selected document; Escape (or the panel's close) clears it
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) setSelectedId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedId]);
  const selected = useMemo(() => {
    if (!selectedId) return null;
    for (const lane of lanes) {
      const doc = lane.docs.find((d) => d.id === selectedId);
      if (doc) return { doc, lane: lane.key, span: spanOf(lane.key, doc) };
    }
    return null;
  }, [selectedId, lanes, spanOf]);
  const openDoc = (id: string) => void host?.open(id);

  // the bars' user counts: the span of each entity over the window's days (one read per kind)
  const idsOf = (kind: EntityKind) => Object.values(entries).flatMap((l) => l.filter((e) => e.kind === kind).map((e) => e.doc.id));
  const windowRange = { from: windowFrom, to: windowTo };
  const figures: Record<string, ReturnType<typeof useEntityFigures>> = {
    event: useEntityFigures('event', idsOf('event'), windowRange),
    offer: useEntityFigures('offer', idsOf('offer'), windowRange),
    offer_group: useEntityFigures('offer_group', idsOf('offer_group'), windowRange),
    ab_test: useEntityFigures('ab_test', idsOf('ab_test'), windowRange),
  };
  const usersOf = (e: Entry): { span: number; title: string } | null => {
    if (!e.kind) return null;
    const f = figures[e.kind];
    const item = f?.get(e.doc.id);
    if (!f || !item) return null;
    return { span: n(item.users.span), title: define(f.definitions, ['span', 'activeUsers', 'testers'], f.computedAt, [`${n(item.users.span)} users active ${f.from} → ${f.to}`]) };
  };

  // Shift + wheel moves a day, Ctrl + Shift + wheel a week (React's wheel listener is passive, so the native one is used)
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;
      e.preventDefault();
      const step = (e.ctrlKey ? 7 : 1) * (e.deltaY > 0 || e.deltaX > 0 ? 1 : -1);
      setStart((s) => s + step * DAY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setStart((s) => s + (e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 7 : 1) * DAY);
    }
  };
  const dayCells = Array.from({ length: days }, (_, i) => window.start + i * DAY);
  const todayStart = windowOf(now, 1, offset).start;
  const pct = (t: number) => `${(((t - window.start) / (window.end - window.start)) * 100).toFixed(3)}%`;
  const dateInput = new Date((start + offset) * 1000).toISOString().slice(0, 10);
  const revenueByDay = new Map((revenue.data?.days ?? []).map((d) => [d.date, d.amounts]));
  const dailyByDay = new Map((daily.data?.days ?? []).map((d) => [d.date, d]));
  const dayKey = (day: number) => utcDate(day + DAY / 2);
  type DailyDay = NonNullable<typeof daily.data>['days'][number];
  const kpiRows: { key: string; label: string; defs: string[]; cell: (d: DailyDay) => string }[] = [
    { key: 'unique', label: 'Unique users', defs: ['uniqueUsers', 'testers'], cell: (d) => String(n(d.uniqueUsers)) },
    { key: 'new', label: 'New users', defs: ['newUsers'], cell: (d) => String(n(d.newUsers)) },
    { key: 'sessions', label: 'Sessions', defs: ['sessions'], cell: (d) => String(n(d.sessions)) },
    { key: 'playtime', label: 'Playtime', defs: ['playtimeSeconds'], cell: (d) => (n(d.playtimeSeconds) ? formatDuration(n(d.playtimeSeconds)) : '0') },
    { key: 'arpdau', label: 'ARPDAU', defs: ['arpdau', 'revenue'], cell: (d) => (d.arpdau === null || d.arpdau === undefined ? '—' : d.arpdau.length ? d.arpdau.map((m) => moneyText(Number(m.amount), m.currency)).join(' ') : '0') },
  ];
  const totals = period.data?.totals;
  const canExport = can('analytics_read', figuresEnv?.slug ?? null);
  const eventTemplates = useMemo(() => {
    const root = catalog.templates.get(EVENT);
    return [root, ...heirsOf(catalog, EVENT)].filter((t): t is NonNullable<typeof t> => !!t && !t.flags.includes('abstract'));
  }, [catalog]);
  const create = (template: string) => {
    const t = catalog.templates.get(template);
    if (!t) return;
    void host?.open(createDocumentDraft(env.workspace, t));
  };
  const toggleType = (t: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  const typeName = (t: string) => catalog.templates.get(t)?.displayName ?? t.split('.').pop();
  const windowRevenue = revenue.data ? (revenue.data.totals.length ? revenue.data.totals.map((m) => moneyText(Number(m.amount), m.currency)).join(' · ') : 'no revenue in the window') : revenue.error ? 'revenue unavailable' : null;
  const filteredOut = search.trim() !== '' || hidden.size > 0;
  return (
    <section className="calendar lo-cal" data-testid="calendar">
      <div className="page-head">
        <h1>Calendar</h1>
        <span className="page-meta tabular">
          {formatInstant(window.start, zone, { offsets }).slice(0, 10)} → {formatInstant(window.end - 1, zone, { offsets }).slice(0, 10)} · {days} days
        </span>
        <span className="spacer" />
        <EnvSelector />
        <Link to={`${routes.branch(org, game.slug, branch, 'liveops/calendar')}?dialog=liveops-settings`} className="btn btn-ghost btn-s" title="LiveOps settings (the comfort payment)">
          <Settings aria-hidden="true" /> Settings
        </Link>
        {canWrite && (
          <Button size="s" variant="primary" onClick={(e) => setCreateAnchor(e.currentTarget)} aria-haspopup="menu" icon={<Plus aria-hidden="true" />}>
            Create <ChevronDown aria-hidden="true" />
          </Button>
        )}
        <Menu
          anchor={createAnchor}
          open={createAnchor !== null}
          onClose={() => setCreateAnchor(null)}
          label="Create"
          items={[
            ...eventTemplates.map((t) => ({ key: t.name, label: t.displayName ?? t.name, onSelect: () => create(t.name) })),
            { key: 's1', label: '', separator: true },
            { key: 'offer', label: 'New offer…', onSelect: () => setWizard('offer') },
            { key: 'group', label: 'Group offer event…', onSelect: () => setWizard('group') },
            { key: 's2', label: '', separator: true },
            { key: 'test', label: 'New A/B test', onSelect: () => void host?.open(createAbTest(env, 'New test').testId) },
          ]}
        />
      </div>
      <div className="page-toolbar cal-toolbar">
        <div className="lo-cal-nav" role="group" aria-label="Move the window">
          <IconButton label="A week back" title="A week back (Shift + ←)" onClick={() => setStart((s) => s - 7 * DAY)}>
            <ChevronsLeft aria-hidden="true" />
          </IconButton>
          <IconButton label="A day back" title="A day back (←)" onClick={() => setStart((s) => s - DAY)}>
            <ChevronLeft aria-hidden="true" />
          </IconButton>
          <Button size="s" variant="ghost" onClick={() => setStart(windowOf(now - 2 * DAY, 1, offset).start)}>
            Today
          </Button>
          <IconButton label="A day forward" title="A day forward (→)" onClick={() => setStart((s) => s + DAY)}>
            <ChevronRight aria-hidden="true" />
          </IconButton>
          <IconButton label="A week forward" title="A week forward (Shift + →)" onClick={() => setStart((s) => s + 7 * DAY)}>
            <ChevronsRight aria-hidden="true" />
          </IconButton>
        </div>
        <input
          type="date"
          className="input input-s input-inline lo-cal-date"
          value={dateInput}
          aria-label="Window start"
          onChange={(e) => {
            const t = Date.parse(`${e.target.value}T00:00:00Z`);
            if (!Number.isNaN(t)) setStart(t / 1000 - offset);
          }}
        />
        <span className="lo-toolbar-sep" aria-hidden="true" />
        <Switch label="Compact (30 days)" checked={compact} onChange={setCompact} />
        <Switch label="Calendar events only" checked={calendarOnly} onChange={setCalendarOnly} />
        <span className="spacer" />
        <span className="lo-search-field">
          <Search aria-hidden="true" />
          <TextInput size="s" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" aria-label="Search the calendar" />
        </span>
      </div>
      {templates.length > 0 && (
        <div className="cal-filters lo-cal-filters" role="group" aria-label="Type filters">
          {templates.map((t) => {
            const on = !hidden.has(t);
            return (
              <button key={t} type="button" className={`chip-toggle${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleType(t)} title={on ? `Hide ${typeName(t)}` : `Show ${typeName(t)}`}>
                <span className="lo-cal-swatch" style={{ background: autoColour(t) }} aria-hidden="true" />
                {typeName(t)}
              </button>
            );
          })}
        </div>
      )}
      <section className="stat-strip lo-cal-stats" data-testid="calendar-metrics" aria-label="Figures of the selected environment">
        <div className="lo-cal-stats-row">
          <div className="stat" title="From the segment sizes of the selected environment">
            <span className="stat-label">Audience</span>
            <span className="stat-value">
              {sizes.data ? (
                <>
                  {sizes.data.players}
                  <span className="muted"> players</span>
                </>
              ) : sizes.loading ? (
                <span className="skeleton lo-skel-stat" aria-label="Loading" />
              ) : (
                '—'
              )}
            </span>
            {sizes.data?.computedAt ? <span className="stat-label">computed {formatInstantWithZone(sizes.data.computedAt, zone, { offsets })}</span> : null}
          </div>
          <div className="stat" title="Claimed, non-test orders of the window, per currency (game-time days)">
            <span className="stat-label">Revenue in the window</span>
            <span className="stat-value">{windowRevenue ?? <span className="skeleton lo-skel-stat" aria-label="Loading" />}</span>
          </div>
          <span className="lo-cal-stats-sep" aria-hidden="true" />
          <div className="lo-cal-kpis" data-testid="calendar-kpis" aria-label="Analytics of the range">
            {period.error ? (
              <span className="lo-note">analytics unavailable ({period.error.message})</span>
            ) : !totals ? (
              ['Unique users', 'New users', 'Sessions', 'Playtime', 'Revenue'].map((l) => (
                <div key={l} className="stat">
                  <span className="stat-label">{l}</span>
                  <span className="skeleton lo-skel-stat" aria-hidden="true" />
                </div>
              ))
            ) : (
              <>
                <div className="stat kpi" data-testid="kpi-unique" title={define(period.data?.definitions, ['uniqueUsers', 'testers'], period.data?.computedAt)}>
                  <span className="stat-label kpi-label">Unique users</span>
                  <span className="stat-value lo-cal-kpi-value">
                    {n(totals.uniqueUsers)}
                    <Sparkline values={(period.data?.days ?? []).map((d) => n(d.uniqueUsers))} label={`Unique users per day, ${range.from} to ${range.to}`} width={64} height={18} />
                  </span>
                </div>
                <div className="stat kpi" data-testid="kpi-new" title={define(period.data?.definitions, ['newUsers'], period.data?.computedAt)}>
                  <span className="stat-label kpi-label">New users</span>
                  <span className="stat-value">{n(totals.newUsers)}</span>
                </div>
                <div className="stat kpi" data-testid="kpi-sessions" title={define(period.data?.definitions, ['sessions'], period.data?.computedAt)}>
                  <span className="stat-label kpi-label">Sessions</span>
                  <span className="stat-value">{n(totals.sessions)}</span>
                </div>
                <div className="stat kpi" data-testid="kpi-playtime" title={define(period.data?.definitions, ['playtimeSeconds'], period.data?.computedAt)}>
                  <span className="stat-label kpi-label">Playtime</span>
                  <span className="stat-value">{n(totals.playtimeSeconds) ? formatDuration(n(totals.playtimeSeconds)) : '0'}</span>
                </div>
                <div className="stat kpi" data-testid="kpi-revenue" title={define(period.data?.definitions, ['revenue'], period.data?.computedAt)}>
                  <span className="stat-label kpi-label">Revenue</span>
                  <span className="stat-value">{totals.revenue.length ? totals.revenue.map((m) => moneyText(Number(m.amount), m.currency)).join(' · ') : '0'}</span>
                </div>
              </>
            )}
          </div>
          <span className="spacer" />
          <div className="lo-cal-stats-controls">
            <RangePicker testers />
            <Button size="s" onClick={() => setExporting(true)} disabled={!figuresEnv} missingPermission={canExport ? null : 'analytics_read'} title="Export the raw analytics events of a range as gzip NDJSON (a job)" icon={<Download aria-hidden="true" />}>
              Export…
            </Button>
          </div>
        </div>
        {totals && !period.error && (
          <span className="lo-cal-computed" data-testid="kpi-computed">
            {range.from} → {range.to}
            {range.includeTesters ? ' · testers included' : ''} · {computed(period.data?.computedAt)}
          </span>
        )}
      </section>
      <div className="lo-cal-body">
      <div className="pane lo-cal-pane">
        <div className="pane-body flush">
          <div className="cal-grid" ref={gridRef} tabIndex={0} onKeyDown={onKeyDown} style={{ ['--days' as string]: days }} aria-label="Timeline">
            <div className="lo-cal-inner">
              <div className="lo-cal-cols" aria-hidden="true">
                {dayCells.map((day) => {
                  const dow = new Date((day + offset) * 1000).getUTCDay();
                  return <span key={day} className={`lo-cal-col${dow === 0 || dow === 6 ? ' weekend' : ''}${day === todayStart ? ' today' : ''}`} />;
                })}
                {now >= window.start && now < window.end && <span className="cal-now" style={{ left: pct(now) }} />}
              </div>
              <div className="cal-row cal-head">
                <span className="cal-label">{zone === 'utc' ? 'game time · UTC' : zone}</span>
                <div className="cal-days">
                  {dayCells.map((day) => {
                    const dow = new Date((day + offset) * 1000).getUTCDay();
                    return (
                      <span key={day} className={`cal-day${dow === 0 || dow === 6 ? ' weekend' : ''}${day === todayStart ? ' today' : ''}`} title={formatInstant(day, zone, { offsets })} aria-current={day === todayStart ? 'date' : undefined}>
                        <span className="lo-cal-dow">{WEEKDAYS[dow]}</span>
                        <span className="lo-cal-day-num">{formatInstant(day, zone, { offsets }).slice(5, 10)}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="cal-row cal-revenue lo-cal-figure" data-testid="calendar-revenue">
                <span className="cal-label" title={daily.data?.definitions?.revenue ?? 'Claimed, non-test orders per game-time day and currency of the selected environment'}>
                  Revenue{revenue.data?.totals.length ? ` · ${revenue.data.totals.map((m) => moneyText(Number(m.amount), m.currency)).join(' · ')}` : revenue.loading && !revenue.data ? ' …' : ''}
                </span>
                <div className="cal-days">
                  {dayCells.map((day) => {
                    const amounts = revenueByDay.get(dayKey(day)) ?? [];
                    return (
                      <span key={day} className="cal-day cal-money" title={amounts.map((m) => moneyText(Number(m.amount), m.currency)).join(', ')}>
                        {amounts.length ? amounts.map((m) => moneyText(Number(m.amount), m.currency)).join(' ') : ''}
                      </span>
                    );
                  })}
                </div>
              </div>
              {kpiRows.map((row) => (
                <div key={row.key} className="cal-row cal-kpi lo-cal-figure" data-testid={`calendar-kpi-${row.key}`}>
                  <span className="cal-label" title={define(daily.data?.definitions, row.defs, daily.data?.computedAt)}>
                    {row.label}
                    {row.key === 'unique' && daily.data ? (
                      <>
                        {' '}
                        <Sparkline values={daily.data.days.map((d) => n(d.uniqueUsers))} label={`Unique users per day, ${windowFrom} to ${windowTo}`} width={48} height={14} />
                      </>
                    ) : null}
                  </span>
                  <div className="cal-days">
                    {dayCells.map((day) => {
                      const d = dailyByDay.get(dayKey(day));
                      return (
                        <span key={day} className="cal-day cal-money">
                          {d ? row.cell(d) : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
              {lanes.map((lane) => {
                const list = entries[lane.key] ?? [];
                const open = !collapsed.has(lane.key);
                return (
                  <div key={lane.key} className="cal-lane" data-testid={`lane-${lane.key}`}>
                    <div className="lo-cal-lane-band">
                      <button
                        type="button"
                        className="cal-lane-head"
                        aria-expanded={open}
                        onClick={() =>
                          setCollapsed((c) => {
                            const next = new Set(c);
                            if (next.has(lane.key)) next.delete(lane.key);
                            else next.add(lane.key);
                            return next;
                          })
                        }
                      >
                        {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                        <span>{lane.label}</span>
                        <span className="lo-cal-count">{list.length}</span>
                      </button>
                    </div>
                    {open &&
                      list.map((e) => {
                        const users = usersOf(e);
                        const isSelected = selectedId === e.doc.id;
                        const pick = {
                          'aria-pressed': isSelected,
                          onClick: () => setSelectedId(e.doc.id),
                          onDoubleClick: () => openDoc(e.doc.id),
                          onKeyDown: (k: KeyboardEvent<HTMLButtonElement>) => {
                            if (k.key === 'Enter') {
                              k.preventDefault();
                              setSelectedId(e.doc.id);
                              openDoc(e.doc.id);
                            }
                          },
                        };
                        const unattached = e.attached !== null && e.attached.length === 0;
                        return (
                          <div key={e.doc.id} className={`cal-row${isSelected ? ' selected' : ''}`} data-testid={`cal-${e.doc.id}`}>
                            <span className="cal-label" title={e.label}>
                              <span className="lo-cal-name">{e.label}</span>
                              {users && (
                                <span className="cal-users" data-testid={`cal-users-${e.doc.id}`} title={users.title}>
                                  <User aria-hidden="true" /> {users.span}
                                </span>
                              )}
                            </span>
                            <div className="cal-track">
                              {unattached && (
                                <button type="button" className={`cal-bar none unattached${isSelected ? ' selected' : ''}`} style={{ left: '0%', width: '100%' }} {...pick} title="Not attached: no game event schedules this offer (attach it from an event's script)" aria-label={`${e.label}: not attached`}>
                                  not attached
                                </button>
                              )}
                              {!e.hasTime && !unattached && (
                                <button type="button" className={`cal-bar none${isSelected ? ' selected' : ''}`} style={{ left: '0%', width: '100%' }} {...pick} title="No time component: always on while its other conditions pass" aria-label={`${e.label}: no time component`}>
                                  no time component
                                </button>
                              )}
                              {e.never && (
                                <span className="cal-bar never" style={{ left: '0%', width: '100%' }} title="The time leaves can never hold together in this window">
                                  never
                                </span>
                              )}
                              {e.tails.map((t, i) => (
                                <span key={`t${i}`} className="cal-bar tail" style={{ left: pct(t.start), width: `calc(${pct(t.end)} - ${pct(t.start)})`, borderColor: e.colour }} title="Duration tail: the event runs this long after its window opens" />
                              ))}
                              {e.bars.map((b, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className={`cal-bar${isSelected ? ' selected' : ''}`}
                                  style={{ left: pct(b.start), width: `calc(${pct(b.end)} - ${pct(b.start)})`, background: e.colour }}
                                  title={`${e.label}\n${formatInstantWithZone(b.start, zone, { offsets })} → ${formatInstantWithZone(b.end, zone, { offsets })}${b.repeated ? '\nrepeat cycle' : ''}${tooltipTail(e.doc)}${users ? `\n${users.title}` : ''}\nclick to select, double-click to open`}
                                  {...pick}
                                  aria-label={`${e.label}: ${formatInstant(b.start, zone, { offsets })} to ${formatInstant(b.end, zone, { offsets })}`}
                                >
                                  {b.repeated ? <Repeat aria-hidden="true" /> : null}
                                  <span className="lo-cal-bar-text">
                                    {e.label}
                                    {users ? ` · ${users.span} users` : ''}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    {open && list.length === 0 && lane.loading && (
                      <div className="cal-row lo-cal-loading" role="status" aria-label={`Loading ${lane.label}`}>
                        <span className="cal-label">
                          <span className="skeleton" />
                        </span>
                        <div className="cal-track">
                          <span className="skeleton" />
                        </div>
                      </div>
                    )}
                    {open && list.length === 0 && !lane.loading && (
                      <div className="lo-cal-empty" role="note">
                        <span className="empty-icon" aria-hidden="true">
                          <CalendarDays />
                        </span>
                        <span className="lo-cal-empty-text">
                          <strong>Nothing in this window</strong>
                          <span>{filteredOut ? `No ${lane.noun} matches the search and the type filters between these dates.` : calendarOnly ? `No ${lane.noun} has a time window between these dates; documents without one are hidden.` : `No ${lane.noun} runs between these dates.`}</span>
                        </span>
                        {filteredOut ? (
                          <Button
                            size="s"
                            onClick={() => {
                              setSearch('');
                              setHidden(new Set());
                            }}
                          >
                            Clear search and filters
                          </Button>
                        ) : calendarOnly ? (
                          <Button size="s" onClick={() => setCalendarOnly(false)}>
                            Show documents without a time
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {selected && (
        <CalendarSide
          key={selected.doc.id}
          doc={selected.doc}
          label={catalog.label(selected.doc)}
          colour={colourOf(selected.doc)}
          typeName={typeName(selected.doc.template) ?? selected.doc.template}
          span={selected.span}
          zone={zone}
          offsets={offsets}
          labelOf={(d) => catalog.label(d)}
          onSelect={setSelectedId}
          onOpen={() => openDoc(selected.doc.id)}
          onClose={() => setSelectedId(null)}
        />
      )}
      </div>
      <div className="cal-legend lo-cal-legend" aria-label="Legend">
        {templates
          .filter((t) => !hidden.has(t))
          .map((t) => (
            <Chip key={t}>
              <span className="lo-cal-swatch" style={{ background: autoColour(t) }} aria-hidden="true" /> {typeName(t)}
            </Chip>
          ))}
        <span className="lo-cal-legend-text">
          bar = the condition's time window · pale tail = the event's duration · <Repeat className="icon-s" aria-label="the repeat icon" /> = a repeat cycle · an offer spans the events that attach it · click to select, double-click to open ·a document's <MoreVertical className="icon-s" aria-label="actions menu" /> sets its colour
        </span>
      </div>
      {wizard === 'offer' && <OfferWizard onClose={() => setWizard(null)} openEvent />}
      {wizard === 'group' && <GroupEventWizard onClose={() => setWizard(null)} />}
      {exporting && figuresEnv && <AnalyticsExportDialog env={figuresEnv.slug} onClose={() => setExporting(false)} />}
    </section>
  );
}

const MAX_SIDE_INTERVALS = 8;

/** The selected bar's side panel (section 8.5): name, template, the window in words, the condition, the attaching events, Open. */
function CalendarSide({ doc, label, colour, typeName, span, zone, offsets, labelOf, onSelect, onOpen, onClose }: { doc: Document; label: string; colour: string; typeName: string; span: Span; zone: DisplayZone; offsets: { dev?: number; stage?: number }; labelOf: (d: Document) => string; onSelect: (id: string) => void; onOpen: () => void; onClose: () => void }) {
  const { bars, attached } = span;
  const condition = dataOf(doc).condition;
  const when = (t: number) => formatInstantWithZone(t, zone, { offsets });
  const windowText =
    attached !== null && attached.length === 0
      ? 'Not attached: no game event schedules this offer, so it does not run on its own.'
      : bars.never
        ? 'Never in this window: the time leaves can never hold together.'
        : !bars.hasTime
          ? 'No time component: always on while its other conditions pass.'
          : bars.intervals.length === 0
            ? 'Not in this window.'
            : null;
  return (
    <aside className="lo-cal-side" aria-label={`Selected: ${label}`} data-testid="calendar-side">
      <div className="lo-cal-side-head">
        <h2>{label}</h2>
        <IconButton label="Close the details" title="Close (Esc)" onClick={onClose}>
          <X aria-hidden="true" />
        </IconButton>
      </div>
      <span className="lo-cal-side-type">
        <span className="lo-cal-swatch" style={{ background: colour }} aria-hidden="true" /> {typeName}
      </span>
      <section aria-label="Window">
        <h3>{windowText ? 'Window' : 'In this window'}</h3>
        {windowText ? (
          <p className="lo-note">{windowText}</p>
        ) : (
          <ul data-testid="calendar-side-window">
            {bars.intervals.slice(0, MAX_SIDE_INTERVALS).map((b, i) => (
              <li key={i}>
                {when(b.start)} → {when(b.end)}
                {b.repeated ? ' (repeat cycle)' : ''}
              </li>
            ))}
            {bars.intervals.length > MAX_SIDE_INTERVALS && <li className="muted">and {bars.intervals.length - MAX_SIDE_INTERVALS} more</li>}
          </ul>
        )}
      </section>
      {attached === null ? (
        <section aria-label="Condition">
          <h3>Condition</h3>
          <ConditionSummary rootId={typeof condition === 'string' ? condition : null} />
        </section>
      ) : (
        attached.length > 0 && (
          <section aria-label="Attached by">
            <h3>Attached by</h3>
            <ul>
              {attached.map((ev) => {
                const c = dataOf(ev).condition;
                return (
                  <li key={ev.id}>
                    <button type="button" className="link-button" onClick={() => onSelect(ev.id)}>
                      {labelOf(ev)}
                    </button>
                    {' · '}
                    <ConditionSummary rootId={typeof c === 'string' ? c : null} />
                  </li>
                );
              })}
            </ul>
          </section>
        )
      )}
      <div className="lo-cal-side-foot">
        <Button size="s" variant="primary" onClick={onOpen}>
          Open
        </Button>
      </div>
    </aside>
  );
}

function tooltipTail(doc: Document): string {
  const d = (doc.data ?? {}) as Record<string, Json>;
  const parts: string[] = [];
  if (typeof d.cooldown === 'number') parts.push(d.cooldown > 0 ? `cooldown ${Math.round(d.cooldown / 3600)}h` : 'no cooldown');
  if (typeof d.limit === 'number') parts.push(d.limit > 0 ? `limit ${d.limit}` : 'unlimited');
  if (typeof d.finishType === 'number') parts.push(['ends with the condition', 'ends after the duration', 'ends from script'][d.finishType] ?? '');
  return parts.length ? `\n${parts.filter(Boolean).join(' · ')}` : '';
}
